import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const DAY_LABELS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'];
const MONTH_LABELS = [
    'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
    'Júl', 'August', 'September', 'Október', 'November', 'December',
];

const pad = (n: number) => String(n).padStart(2, '0');

function parseIso(value?: string): { year: number; month: number; day: number } | null {
    if (!value) return null;
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return { year: y, month: m - 1, day: d };
}

function toIso(year: number, month: number, day: number): string {
    return `${year}-${pad(month + 1)}-${pad(day)}`;
}

function formatDisplay(value?: string): string {
    const parsed = parseIso(value);
    if (!parsed) return '';
    return `${pad(parsed.day)}.${pad(parsed.month + 1)}.${parsed.year}`;
}

type DatePickerProps = {
    value?: string;
    onChange: (isoDate: string) => void;
    accentColor?: string;
    id?: string;
    placeholder?: string;
};

export default function DatePicker({
                                        value,
                                        onChange,
                                        accentColor = 'var(--st-accent)',
                                        id,
                                        placeholder = 'Vyberte dátum',
                                    }: DatePickerProps) {
    const selected = parseIso(value);
    const today = new Date();

    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState(selected?.year ?? today.getFullYear());
    const [viewMonth, setViewMonth] = useState(selected?.month ?? today.getMonth());
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open]);

    const openCalendar = () => {
        setViewYear(selected?.year ?? today.getFullYear());
        setViewMonth(selected?.month ?? today.getMonth());
        setOpen(v => !v);
    };

    const shiftMonth = (delta: number) => {
        let m = viewMonth + delta;
        let y = viewYear;
        if (m < 0) { m = 11; y -= 1; }
        if (m > 11) { m = 0; y += 1; }
        setViewMonth(m);
        setViewYear(y);
    };

    const pick = (day: number, month: number, year: number) => {
        onChange(toIso(year, month, day));
        setOpen(false);
    };

    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7; // Monday-first offset
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells: { day: number; month: number; year: number; inMonth: boolean }[] = [];

    for (let i = leadingBlanks - 1; i >= 0; i--) {
        const month = viewMonth === 0 ? 11 : viewMonth - 1;
        const year = viewMonth === 0 ? viewYear - 1 : viewYear;
        cells.push({ day: daysInPrevMonth - i, month, year, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ day: d, month: viewMonth, year: viewYear, inMonth: true });
    }
    const trailingMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const trailingYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    let trailingDay = 1;
    while (cells.length % 7 !== 0 || cells.length < 42) {
        cells.push({ day: trailingDay, month: trailingMonth, year: trailingYear, inMonth: false });
        trailingDay++;
    }

    const isSameDay = (c: { day: number; month: number; year: number }) =>
        !!selected && selected.day === c.day && selected.month === c.month && selected.year === c.year;

    const isToday = (c: { day: number; month: number; year: number }) =>
        c.day === today.getDate() && c.month === today.getMonth() && c.year === today.getFullYear();

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                id={id}
                onClick={openCalendar}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: '0.6rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--st-radius-ctl)',
                    border: `1.5px solid ${open ? accentColor : 'var(--st-line-strong)'}`,
                    backgroundColor: 'var(--st-paper)',
                    color: selected ? 'var(--st-text)' : 'var(--st-text-faint)',
                    fontFamily: 'var(--st-font-body)',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease',
                }}
            >
                <span>{selected ? formatDisplay(value) : placeholder}</span>
                <FontAwesomeIcon icon={faCalendarDay} style={{ color: accentColor, fontSize: '0.9rem' }} />
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 0.4rem)',
                        left: 0,
                        zIndex: 1050,
                        width: '280px',
                        padding: '0.85rem',
                        borderRadius: 'var(--st-radius-card)',
                        border: '1px solid var(--st-line)',
                        backgroundColor: 'var(--st-paper)',
                        boxShadow: 'var(--st-shadow-pop)',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <NavButton onClick={() => shiftMonth(-1)} accentColor={accentColor}>
                            <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.7rem' }} />
                        </NavButton>

                        <span style={{
                            fontFamily: 'var(--st-font-display)',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            color: 'var(--st-text)',
                        }}>
                            {MONTH_LABELS[viewMonth]} {viewYear}
                        </span>

                        <NavButton onClick={() => shiftMonth(1)} accentColor={accentColor}>
                            <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.7rem' }} />
                        </NavButton>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '0.15rem' }}>
                        {DAY_LABELS.map(label => (
                            <span key={label} style={{
                                textAlign: 'center',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                color: 'var(--st-text-faint)',
                                padding: '0.2rem 0',
                            }}>
                                {label}
                            </span>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: '0.15rem' }}>
                        {cells.map((c, idx) => {
                            const active = isSameDay(c);
                            const todayCell = isToday(c) && !active;
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => pick(c.day, c.month, c.year)}
                                    style={{
                                        width: '100%',
                                        aspectRatio: '1 / 1',
                                        border: todayCell ? `1.5px solid ${accentColor}` : '1px solid transparent',
                                        borderRadius: '8px',
                                        backgroundColor: active ? accentColor : 'transparent',
                                        color: active ? 'white' : c.inMonth ? 'var(--st-text)' : 'var(--st-text-faint)',
                                        fontWeight: active || todayCell ? 700 : 400,
                                        fontSize: '0.82rem',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.12s ease',
                                    }}
                                    onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'var(--st-hover)'; }}
                                    onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                    {c.day}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            const now = new Date();
                            pick(now.getDate(), now.getMonth(), now.getFullYear());
                        }}
                        style={{
                            marginTop: '0.6rem',
                            width: '100%',
                            padding: '0.35rem',
                            border: 'none',
                            borderRadius: 'var(--st-radius-ctl)',
                            backgroundColor: 'transparent',
                            color: accentColor,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                        }}
                    >
                        Dnes
                    </button>
                </div>
            )}
        </div>
    );
}

const NavButton: React.FC<{ onClick: () => void; accentColor: string; children: React.ReactNode }> = ({ onClick, accentColor, children }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '7px',
                border: 'none',
                backgroundColor: hovered ? 'var(--st-hover)' : 'transparent',
                color: hovered ? accentColor : 'var(--st-text-soft)',
                cursor: 'pointer',
            }}
        >
            {children}
        </button>
    );
};